import React, { useState, useCallback, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { videoUploadHandler } from '../CustomHandler/CustomHandler';

type Tab = 'link' | 'upload';

type Props = {
  quillRef: React.MutableRefObject<any>;
  collectionPath: string;
  setVideoURLList?: (updater: (prev: string[]) => string[]) => void;
  onClose: () => void;
  direction?: 'up' | 'down';
};

function parseYouTubeStartSeconds(raw?: string | null): string | null {
  if (!raw) return null;
  const value = raw.trim();
  if (!value) return null;

  // 90, 90s, 1m30s, 1h2m3s 모두 지원
  const direct = Number(value.replace(/s$/i, ''));
  if (!Number.isNaN(direct)) return String(Math.max(0, Math.floor(direct)));

  const match = value.match(
    /^(?:(\d+)h)?(?:(\d+)m)?(?:(\d+)s)?$/i,
  );
  if (!match) return null;
  const h = Number(match[1] || 0);
  const m = Number(match[2] || 0);
  const s = Number(match[3] || 0);
  const total = h * 3600 + m * 60 + s;
  return total > 0 ? String(total) : null;
}

function normalizeVideoUrl(rawUrl: string): string {
  if (!rawUrl) return '';

  let normalized = rawUrl.trim();

  // 프로토콜이 없으면 https를 기본으로 붙인다.
  if (!/^https?:\/\//i.test(normalized)) {
    normalized = `https://${normalized}`;
  }

  try {
    const url = new URL(normalized);
    const host = url.hostname.replace(/^(www|m|music)\./, '');
    const pathname = url.pathname;

    // youtube.com/watch?v=...
    if (host === 'youtube.com' && pathname === '/watch') {
      const videoId = url.searchParams.get('v');
      if (!videoId) return normalized;
      const start = parseYouTubeStartSeconds(
        url.searchParams.get('start') || url.searchParams.get('t'),
      );
      const embed = new URL(`https://www.youtube.com/embed/${videoId}`);
      if (start) embed.searchParams.set('start', start);
      return embed.toString();
    }

    // youtu.be/...
    if (host === 'youtu.be') {
      const videoId = pathname.replace('/', '').split('/')[0];
      if (!videoId) return normalized;
      const start = parseYouTubeStartSeconds(
        url.searchParams.get('start') || url.searchParams.get('t'),
      );
      const embed = new URL(`https://www.youtube.com/embed/${videoId}`);
      if (start) embed.searchParams.set('start', start);
      return embed.toString();
    }

    // youtube.com/shorts/... 또는 /live/...
    if (host === 'youtube.com' && (pathname.startsWith('/shorts/') || pathname.startsWith('/live/'))) {
      const videoId = pathname.split('/')[2];
      if (!videoId) return normalized;
      return `https://www.youtube.com/embed/${videoId}`;
    }

    return normalized;
  } catch {
    return '';
  }
}

function VideoInsertDropdown({
  quillRef,
  collectionPath,
  setVideoURLList,
  onClose,
  direction = 'down',
}: Props) {
  const [activeTab, setActiveTab] = useState<Tab>('link');
  const [urlValue, setUrlValue] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // 외부 클릭 시 닫기
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  const getQuillEditor = useCallback(() => {
    if (!quillRef.current) return null;
    try {
      return quillRef.current.getEditor();
    } catch {
      return null;
    }
  }, [quillRef]);

  const handleLinkConfirm = useCallback(() => {
    const url = normalizeVideoUrl(urlValue);
    if (!url) return;

    const quill = getQuillEditor();
    if (!quill) return;

    const range = quill.getSelection(true);
    const index = range ? range.index : 0;

    quill.insertEmbed(index, 'video', url, 'user');
    quill.setSelection(index + 1);

    onClose();
  }, [urlValue, getQuillEditor, onClose]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') handleLinkConfirm();
      if (e.key === 'Escape') onClose();
    },
    [handleLinkConfirm, onClose],
  );

  const handleFileUpload = useCallback(async () => {
    const quill = getQuillEditor();
    if (!quill) return;

    setIsUploading(true);
    try {
      await videoUploadHandler({
        quill,
        setVideoURLList,
        collectionPath,
      });
    } finally {
      setIsUploading(false);
      onClose();
    }
  }, [getQuillEditor, collectionPath, setVideoURLList, onClose]);

  return (
    <DropdownContainer
      ref={dropdownRef}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      $direction={direction}
    >
      <TabRow>
        <TabBtn
          type="button"
          $active={activeTab === 'link'}
          onClick={() => setActiveTab('link')}
        >
          링크 입력
        </TabBtn>
        <TabBtn
          type="button"
          $active={activeTab === 'upload'}
          onClick={() => setActiveTab('upload')}
        >
          직접 업로드
        </TabBtn>
      </TabRow>

      {activeTab === 'link' && (
        <PanelWrap>
          <UrlInput
            type="text"
            placeholder="동영상 URL을 입력하세요"
            value={urlValue}
            onChange={(e) => setUrlValue(e.target.value)}
            onKeyDown={handleKeyDown}
            autoFocus
          />
          <ConfirmBtn type="button" onClick={handleLinkConfirm}>
            삽입
          </ConfirmBtn>
        </PanelWrap>
      )}

      {activeTab === 'upload' && (
        <PanelWrap>
          <UploadDescription>
            MP4, MOV, AVI, WebM 등 동영상 파일 (최대 500MB)
          </UploadDescription>
          <UploadBtn
            type="button"
            onClick={handleFileUpload}
            disabled={isUploading}
          >
            {isUploading ? '업로드 중...' : '파일 선택'}
          </UploadBtn>
        </PanelWrap>
      )}
    </DropdownContainer>
  );
}

export default React.memo(VideoInsertDropdown);

// ─── Styled ───────────────────────────────────────────────
const DropdownContainer = styled.div<{ $direction?: 'up' | 'down' }>`
  position: absolute;
  ${({ $direction }) =>
    $direction === 'up'
      ? 'bottom: calc(100% + 6px); top: auto;'
      : 'top: calc(100% + 6px);'}
  left: 50%;
  transform: translateX(-50%);
  z-index: 100;
  background: var(--cardBackgroundColor);
  border: 1px solid var(--lineColor);
  border-radius: 8px;
  box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.12);
  padding: 8px;
  width: 280px;
`;

const TabRow = styled.div`
  display: flex;
  gap: 2px;
  margin-bottom: 8px;
`;

const TabBtn = styled.button<{ $active: boolean }>`
  flex: 1 !important;
  width: auto !important;
  height: 30px !important;
  font-size: 13px;
  font-weight: ${({ $active }) => ($active ? 700 : 400)};
  background: ${({ $active }) =>
    $active ? 'var(--primaryColorLight, rgba(0,0,0,0.06))' : 'transparent'};
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: 0.15s;

  && {
    color: ${({ $active }) =>
      $active ? 'var(--primaryColor)' : 'var(--subTextColor)'} !important;
  }

  &:hover {
    background: var(--primaryColorLight, rgba(0, 0, 0, 0.06));
    && {
      color: var(--primaryColor) !important;
    }
  }
`;

const PanelWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const UrlInput = styled.input`
  width: 100%;
  height: 36px;
  padding: 0 10px;
  border: 1px solid var(--lineColor);
  border-radius: 6px;
  font-size: 13px;
  background: var(--backgroundColor);
  color: var(--textColor);
  outline: none;
  box-sizing: border-box;

  &::placeholder {
    color: var(--subTextColor);
  }

  &:focus {
    border-color: var(--primaryColor);
  }
`;

const ConfirmBtn = styled.button`
  width: 100% !important;
  height: 36px !important;
  background: var(--buttonBackgroundColor);
  font-size: 13px;
  font-weight: 700;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: 0.15s;

  && {
    color: var(--buttonTextColor) !important;
  }

  &:hover {
    opacity: 0.85;
  }
`;

const UploadDescription = styled.p`
  font-size: 12px;
  color: var(--subTextColor);
  margin: 0;
  line-height: 1.5;
`;

const UploadBtn = styled.button`
  width: 100% !important;
  height: 36px !important;
  background: var(--buttonBackgroundColor);
  font-size: 13px;
  font-weight: 700;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: 0.15s;

  && {
    color: var(--buttonTextColor) !important;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:not(:disabled):hover {
    opacity: 0.85;
  }
`;
